/**
 * La app del técnico.
 *
 * ── Qué es y por qué vive aquí ──
 *
 * Cuando entra alguien con rol «técnico», el sistema NO le muestra el menú de escritorio:
 * le muestra esto. Misma dirección, mismo usuario, misma instalación en el celular — pero
 * una app distinta, hecha para una mano y para la calle.
 *
 * Va dentro del sistema y no en un sitio aparte porque un sitio aparte tendría que repetir
 * el inicio de sesión, el manejo de errores y las fotos, y serían dos cosas que mantener en
 * vez de una. Además el certificado de Cloudflare solo cubre tres dominios (Full strict), y
 * un subdominio nuevo rompe hasta ampliarlo.
 *
 * ── Sin señal: se ve, no se guarda ──
 *
 * Decidido por Víctor. El trabajo del día se guarda en el celular para poder leerlo en una
 * vereda sin señal, SIEMPRE diciendo de cuándo es. Lo que no se hace es guardar cambios para
 * subirlos después: si dos personas tocan el mismo ticket gana el último en subir y el otro
 * trabajo se pierde sin que nadie se entere. Un error silencioso en el trabajo de campo es
 * peor que un «no se pudo, vuelva a intentar».
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { COLOR, s } from './estilos';
import { guardarElDia, leerElDia, olvidarElDia } from './guardado';
import MiDia from './MiDia';
import FichaTicket from './FichaTicket';

/** Lo terminado, para poder mirar atrás. No entra en «mi día». */
function Historial({ hayRed, onAbrir }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get('/api/tickets', { params: { estado: 'resuelto' } })
      .then(({ data }) => { if (vivo) setLista(data); })
      .catch(() => { if (vivo) setLista([]); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  if (cargando) return <div style={s.vacio}>Buscando…</div>;
  if (!hayRed && !lista.length) {
    return <div style={s.vacio}>📵 Lo terminado necesita señal para verse.</div>;
  }
  if (!lista.length) return <div style={s.vacio}>Todavía no ha terminado ningún trabajo.</div>;

  return (
    <div style={s.cuerpo}>
      <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', color: COLOR.verde, textTransform: 'uppercase' }}>
        Terminados · {lista.length}
      </div>
      {lista.map((t) => (
        <button key={t.id} onClick={() => onAbrir(t.id)}
          style={{ ...s.tarjeta, textAlign: 'left', cursor: 'pointer', color: COLOR.texto, width: '100%' }}>
          <div style={{ ...s.titulo, fontSize: '15px' }}>{t.titulo}</div>
          <div style={{ ...s.sub, marginTop: '4px' }}>
            N.º {t.numero}{t.cliente_nombre ? ` · ${t.cliente_nombre}` : ''}
          </div>
          {/* Quién lo resolvió: aquí salen los de todo el equipo, y sin el nombre no se
              distingue el trabajo propio del ajeno. */}
          {(t.fecha_cierre || t.resuelto_por_nombre) && (
            <div style={{ ...s.sub, marginTop: '2px' }}>
              ✅ {t.fecha_cierre
                    ? new Date(t.fecha_cierre).toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })
                    : 'Terminado'}
              {t.resuelto_por_nombre ? ` · ${String(t.resuelto_por_nombre).split(' ')[0]}` : ''}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

/** Los datos del usuario y el botón de salir. Nada del negocio. */
function Yo({ usuario, onSalir, resumen }) {
  return (
    <div style={{ ...s.cuerpo, gap: '14px' }}>
      <div style={{ ...s.tarjeta, textAlign: 'center', padding: '24px 14px' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 12px',
          background: 'rgba(0,229,255,0.12)', color: COLOR.acento,
          display: 'grid', placeItems: 'center', fontSize: '26px', fontWeight: 700,
        }}>
          {(usuario?.nombre || '?').trim().charAt(0).toUpperCase()}
        </div>
        <div style={{ ...s.titulo, fontSize: '17px' }}>{usuario?.nombre}</div>
        <div style={{ ...s.sub, marginTop: '4px' }}>Técnico · LinkUs Telecomunicaciones</div>
      </div>

      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '10px' }}>
          {[
            /* Esta es SU pantalla, así que los números son suyos: lo que tiene asignado y lo
               que ha resuelto él. El trabajo del equipo se cuenta en la cabecera de
               «Trabajos» — mezclarlos aquí le diría «14 terminados» cuando hizo tres. */
            ['Míos pendientes', resumen.mios_pendientes, COLOR.ambar],
            ['Del equipo', resumen.abiertos, COLOR.morado],
            ['Resueltos por mí', resumen.resueltos_por_mi, COLOR.verde],
          ].map(([txt, n, color]) => (
            <div key={txt} style={{ ...s.tarjeta, textAlign: 'center', padding: '14px 6px' }}>
              <div style={{ fontSize: '24px', fontWeight: 800, color }}>{n ?? 0}</div>
              <div style={{ ...s.sub, fontSize: '11px', marginTop: '2px' }}>{txt}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...s.sub, fontSize: '12px', textAlign: 'center', lineHeight: 1.6 }}>
        Para tener esto como aplicación: abra el menú del navegador y toque
        «Agregar a la pantalla de inicio».
      </div>

      <button
        style={{ ...s.botonSuave, borderColor: 'rgba(239,68,68,0.35)', color: '#FCA5A5' }}
        onClick={onSalir}
      >
        Cerrar sesión
      </button>
    </div>
  );
}

export default function AppTecnico() {
  const { usuario, logout } = useAuth();
  const [pestana, setPestana] = useState('hoy');
  const [abierto, setAbierto] = useState(null);
  const [datos, setDatos] = useState(null);
  const [deGuardado, setDeGuardado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [hayRed, setHayRed] = useState(navigator.onLine);

  /* `navigator.onLine` dice si hay red, no si el servidor contesta: en una vereda con una
     raya de señal el celular se cree conectado. Por eso además de escucharlo, cada vez que
     una petición falla se marca que no hay — lo que manda es si de verdad llegó. */
  useEffect(() => {
    const conectado = () => setHayRed(true);
    const caido = () => setHayRed(false);
    window.addEventListener('online', conectado);
    window.addEventListener('offline', caido);
    return () => {
      window.removeEventListener('online', conectado);
      window.removeEventListener('offline', caido);
    };
  }, []);

  const traerDia = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/api/tickets/mi-dia');
      setDatos(data);
      setDeGuardado(false);
      setHayRed(true);
      guardarElDia(data);
    } catch (e) {
      const guardado = leerElDia();
      if (guardado) { setDatos(guardado); setDeGuardado(true); }
      setHayRed(false);
    } finally { setCargando(false); }
  }, []);

  const traerResumen = useCallback(() => {
    api.get('/api/tickets/stats/resumen')
      .then(({ data }) => setResumen(data))
      .catch(() => { /* el resumen es de adorno: si no llega, la pantalla sigue sirviendo */ });
  }, []);

  useEffect(() => { traerDia(); traerResumen(); }, [traerDia, traerResumen]);

  /* Al volver a la app después de un rato (el técnico la deja abierta entre casa y casa) se
     vuelve a pedir el día: si no, se queda enseñando lo de la mañana toda la tarde. */
  useEffect(() => {
    const alVolver = () => { if (document.visibilityState === 'visible') traerDia(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [traerDia]);

  const salir = () => {
    // Lo del negocio no se queda en el teléfono: un celular de técnico se pierde o se vende.
    olvidarElDia();
    logout();
  };

  const trasCambio = () => { traerDia(); traerResumen(); };

  const pendientes = datos?.tickets?.length || 0;

  return (
    <div style={s.pantalla}>
      <div style={s.cabecera}>
        {abierto ? (
          <button
            onClick={() => setAbierto(null)}
            style={{ background: 'none', border: 'none', color: COLOR.acento, fontSize: '15px',
                     fontWeight: 700, cursor: 'pointer', padding: '6px 4px', minHeight: '40px' }}
          >← Volver</button>
        ) : (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '17px', fontWeight: 800, overflowWrap: 'anywhere' }}>
              {pestana === 'hoy' ? 'Trabajos' : pestana === 'hechos' ? 'Terminados' : 'Mi cuenta'}
            </div>
            {pestana === 'hoy' && (
              <div style={{ ...s.sub, fontSize: '12px' }}>
                {usuario?.nombre?.split(' ')[0]} · {pendientes} pendiente{pendientes === 1 ? '' : 's'} en el equipo
              </div>
            )}
          </div>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <span style={{
            ...s.chip, fontSize: '11px',
            background: hayRed ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.14)',
            color: hayRed ? COLOR.verde : COLOR.ambar,
          }}>
            {hayRed ? '● En línea' : '📵 Sin señal'}
          </span>
        </div>
      </div>

      {abierto ? (
        <FichaTicket
          ticketId={abierto}
          hayRed={hayRed}
          /* Lo que ya se trajo de ese trabajo, para poder armar la ficha sin señal. */
          deLaLista={datos?.tickets?.find((t) => t.id === abierto) || null}
          onVolver={() => setAbierto(null)}
          onCambio={trasCambio}
        />
      ) : pestana === 'hoy' ? (
        <MiDia
          datos={datos} hayRed={hayRed} deGuardado={deGuardado} cargando={cargando}
          onAbrir={setAbierto} onRefrescar={traerDia}
        />
      ) : pestana === 'hechos' ? (
        <Historial hayRed={hayRed} onAbrir={setAbierto} />
      ) : (
        <Yo usuario={usuario} onSalir={salir} resumen={resumen} />
      )}

      {/* La barra de abajo no se enseña con un trabajo abierto: ahí lo único que se puede
          hacer es volver, y una barra de navegación invita a irse a medio guardar. */}
      {!abierto && (
        <div style={s.barra}>
          {[
            ['hoy', '📋', 'Trabajos'],
            ['hechos', '✅', 'Terminados'],
            ['yo', '👤', 'Mi cuenta'],
          ].map(([id, icono, txt]) => (
            <button
              key={id}
              onClick={() => setPestana(id)}
              style={{ ...s.barraBoton, color: pestana === id ? COLOR.acento : COLOR.suave }}
            >
              <span style={{ fontSize: '20px' }}>{icono}</span>
              <span>{txt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
